import { useState, useEffect } from 'react';
import { 
  getReservations, 
  getServices, 
  createReservationAdmin,
  updateReservation,
  cancelReservationAdmin,
  deleteReservation,
  getBlockedTimes,
  getSettings
} from '../utils/api';
import { closeModalWithAnimation } from '../utils/modalHelpers';
import { formatPhoneNumber } from '../utils/formatters';
import ConfirmModal from '../components/ConfirmModal';
import AdminPageSkeleton from '../components/AdminPageSkeleton';
import ReservationCalendar from '../components/ReservationCalendar';
import AddressAutocompleteField from '../components/AddressAutocompleteField';
import Error500 from './errors/Error500';
import './AdminPages.css';

const AdminReservationsPage = () => {
  const [reservations, setReservations] = useState([]);
  const [services, setServices] = useState([]);
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [serverError, setServerError] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedService, setSelectedService] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [editingReservation, setEditingReservation] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [selectedIds, setSelectedIds] = useState([]);
  const [quickFilterValue, setQuickFilterValue] = useState('');
  const [modalError, setModalError] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [fieldWarnings, setFieldWarnings] = useState({});
  const [originalFormData, setOriginalFormData] = useState(null);
  const [overlapConfirmPending, setOverlapConfirmPending] = useState(false);
  const [openActionId, setOpenActionId] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState({
    top: 0,
    left: 0,
    transform: 'translateX(-100%)'
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalTitle, setConfirmModalTitle] = useState('');
  const [confirmModalMessage, setConfirmModalMessage] = useState('');
  const [confirmModalAction, setConfirmModalAction] = useState(null);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState('success');
  const [calendarSettings, setCalendarSettings] = useState({
    slotMinTime: '07:00:00',
    slotMaxTime: '20:00:00'
  });
  const [workingHours, setWorkingHours] = useState([]);
  
  const [sortField, setSortField] = useState('default');
  const [sortDirection, setSortDirection] = useState('asc');
  
  const itemsPerPage = 15;

  const [formData, setFormData] = useState({
    cele_meno: '',
    email: '',
    telefon: '',
    adresa: '',
    datum_rezervacie: '',
    cas_rezervacie: '',
    sluzba_id: '',
    poznamka: '',
  });

  useEffect(() => {
    loadSettings();
    loadReservations();
    loadServices();
    loadBlockedTimes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.actions-dropdown')) {
        setOpenActionId(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!openActionId) return;
    const handleClose = () => setOpenActionId(null);
    window.addEventListener('scroll', handleClose, true);
    window.addEventListener('resize', handleClose);
    return () => {
      window.removeEventListener('scroll', handleClose, true);
      window.removeEventListener('resize', handleClose);
    };
  }, [openActionId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchTerm, dateFrom, dateTo, selectedService, quickFilterValue]);

  const loadSettings = async () => {
    try {
      const response = await getSettings();
      const data = response.data.data || response.data;
      const settings = data.settings || {};
      
      if (settings.default_reservations_view) {
        setViewMode(settings.default_reservations_view);
      }
      
      if (settings.default_calendar_view) {
        localStorage.setItem('default_calendar_view', settings.default_calendar_view);
      }
      
      setCalendarSettings({
        slotMinTime: settings.calendar_slot_min_time ? `${settings.calendar_slot_min_time}:00` : '07:00:00',
        slotMaxTime: settings.calendar_slot_max_time ? `${settings.calendar_slot_max_time}:00` : '20:00:00'
      });

      setWorkingHours(data.working_hours || []);
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
  };

  const loadBlockedTimes = async () => {
    try {
      const response = await getBlockedTimes();
      const blockedData = response.data.data || response.data || [];
      setBlockedTimes(blockedData);
    } catch (err) {
      console.error('Failed to load blocked times:', err);
      setBlockedTimes([]);
    }
  };

  const loadServices = async () => {
    try {
      const response = await getServices();
      const servicesData = response.data.data || response.data || [];
      setServices(servicesData);
    } catch (err) {
      console.error('Failed to load services:', err);
    }
  };

  const loadReservations = async () => {
    try {
      setLoading(true);
      const response = await getReservations();
      const reservationsData = response.data.data || response.data || [];
      setReservations(reservationsData);
      setError(null);
      setServerError(false);
    } catch (err) {
      console.error('Failed to load reservations:', err);
      if (!err.response || err.response.status >= 500 || err.code === 'ERR_NETWORK') {
        setServerError(true);
        return;
      }
      setError('Nepodarilo sa načítať rezervácie. Skúste to prosím neskôr.');
    } finally {
      setLoading(false);
    }
  };

  const getReservationDateTime = (reservation) => (
    new Date(`${reservation.datum_rezervacie}T${reservation.cas_rezervacie}`)
  );

  const matchesTabFilter = (reservation, activeFilter) => {
    const now = new Date();
    const reservationDateTime = getReservationDateTime(reservation);

    if (activeFilter === 'pending') {
      return reservation.stav === 'pending';
    }

    if (activeFilter === 'upcoming') {
      return reservationDateTime >= now && reservation.stav === 'confirmed';
    }

    if (activeFilter === 'past') {
      return (reservationDateTime < now || reservation.stav === 'completed') && reservation.stav !== 'cancelled';
    }

    if (activeFilter === 'cancelled') {
      return reservation.stav === 'cancelled';
    }

    return true;
  };

  const applyNonStatusFilters = (reservationList) => {
    let filtered = [...reservationList];

    if (dateFrom) {
      filtered = filtered.filter(r => r.datum_rezervacie >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(r => r.datum_rezervacie <= dateTo);
    }
   
    if (selectedService !== 'all') {
      filtered = filtered.filter(r => r.sluzba_id === parseInt(selectedService));
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase().replace(/\s/g, '');
      filtered = filtered.filter(r => {
        const nameMatch = r.cele_meno?.toLowerCase().includes(searchTerm.toLowerCase());
        const emailMatch = r.email?.toLowerCase().includes(searchTerm.toLowerCase());
        const addressMatch = r.adresa?.toLowerCase().includes(searchTerm.toLowerCase());
        const phoneNormalized = r.telefon?.replace(/\s/g, '') || '';
        const phoneMatch = phoneNormalized.includes(searchLower);
        const reservationIdMatch = r.rezervacne_id?.toLowerCase().includes(searchTerm.toLowerCase());
        
        return nameMatch || emailMatch || phoneMatch || addressMatch || reservationIdMatch;
      });
    }

    filtered.sort((a, b) => {
      const dateTimeA = getReservationDateTime(a);
      const dateTimeB = getReservationDateTime(b);
      return dateTimeA - dateTimeB;
    });

    return filtered;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortReservations = (reservations) => {
    const compareDateTime = (a, b, direction = 'asc') => {
      const aValue = getReservationDateTime(a).getTime();
      const bValue = getReservationDateTime(b).getTime();

      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    };

    const getSmartSortGroup = (reservation) => {
      const reservationDateTime = getReservationDateTime(reservation);
      const now = new Date();

      if (reservation.stav === 'pending') return 0;
      if (reservation.stav === 'confirmed' && reservationDateTime >= now) return 1;
      if (reservation.stav === 'completed' || reservationDateTime < now) return 2;
      if (reservation.stav === 'cancelled') return 3;
      return 4;
    };

    return [...reservations].sort((a, b) => {
      if (sortField === 'default') {
        if (filter === 'past' || filter === 'cancelled') {
          return compareDateTime(a, b, 'desc');
        }

        if (filter === 'pending' || filter === 'upcoming') {
          return compareDateTime(a, b, 'asc');
        }

        const groupDifference = getSmartSortGroup(a) - getSmartSortGroup(b);
        if (groupDifference !== 0) {
          return groupDifference;
        }

        const group = getSmartSortGroup(a);

        if (group === 0 || group === 1) {
          return compareDateTime(a, b, 'asc');
        }

        return compareDateTime(a, b, 'desc');
      }

      let aValue, bValue;

      switch (sortField) {
        case 'cele_meno':
          aValue = a.cele_meno?.toLowerCase() || '';
          bValue = b.cele_meno?.toLowerCase() || '';
          break;
        case 'telefon':
          aValue = a.telefon || '';
          bValue = b.telefon || '';
          break;
        case 'datum_cas':
          aValue = new Date(`${a.datum_rezervacie}T${a.cas_rezervacie}`);
          bValue = new Date(`${b.datum_rezervacie}T${b.cas_rezervacie}`);
          break;
        case 'vytvorene':
          aValue = new Date(a.vytvorene || a.created_at || a.createdAt || 0).getTime() || 0;
          bValue = new Date(b.vytvorene || b.created_at || b.createdAt || 0).getTime() || 0;
          break;
        case 'sluzba_nazov':
        case 'nazov_sluzby':
          aValue = a.nazov_sluzby?.toLowerCase() || '';
          bValue = b.nazov_sluzby?.toLowerCase() || '';
          break;
        case 'stav':
          aValue = a.stav || '';
          bValue = b.stav || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const baseFilteredReservations = applyNonStatusFilters(reservations);

  const counts = {
    all: baseFilteredReservations.length,
    pending: baseFilteredReservations.filter((reservation) => matchesTabFilter(reservation, 'pending')).length,
    upcoming: baseFilteredReservations.filter((reservation) => matchesTabFilter(reservation, 'upcoming')).length,
    past: baseFilteredReservations.filter((reservation) => matchesTabFilter(reservation, 'past')).length,
    cancelled: baseFilteredReservations.filter((reservation) => matchesTabFilter(reservation, 'cancelled')).length,
  };

  const handleQuickFilterChange = (e) => {
    const value = e.target.value;
    setQuickFilterValue(value);
    
    if (!value) {
      setDateFrom('');
      setDateTo('');
      return;
    }
    
    const formatDateLocal = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const date = today.getDate();
    
    switch(value) {
      case 'today':
        const todayStr = formatDateLocal(today);
        setDateFrom(todayStr);
        setDateTo(todayStr);
        break;
      case 'tomorrow':
        const tomorrow = new Date(year, month, date + 1);
        const tomorrowStr = formatDateLocal(tomorrow);
        setDateFrom(tomorrowStr);
        setDateTo(tomorrowStr);
        break;
      case 'week':
        const weekStart = new Date(year, month, date - today.getDay() + 1); // Pondelok
        const weekEnd = new Date(year, month, date - today.getDay() + 7); // Nedeľa
        setDateFrom(formatDateLocal(weekStart));
        setDateTo(formatDateLocal(weekEnd));
        break;
      case 'month':
        const monthStart = new Date(year, month, 1);
        const monthEnd = new Date(year, month + 1, 0);
        setDateFrom(formatDateLocal(monthStart));
        setDateTo(formatDateLocal(monthEnd));
        break;
      default:
        break;
    }
  };

  const handleCloseModal = () => {
    setOverlapConfirmPending(false);
    closeModalWithAnimation(() => setShowModal(false));
  };

  const handleCloseConfirmModal = () => {
    closeModalWithAnimation(() => setShowConfirmModal(false));
  };

  const handleCloseNotificationModal = () => {
    closeModalWithAnimation(() => setShowNotificationModal(false));
  };

  const handleAddReservation = () => {
    setModalMode('add');
    setModalError('');
    setFieldErrors({});
    setOverlapConfirmPending(false);
    setFormData({
      cele_meno: '',
      email: '',
      telefon: '',
      adresa: '',
      datum_rezervacie: '',
      cas_rezervacie: '',
      sluzba_id: '',
      poznamka: '',
    });
    setShowModal(true);
  };

  const handleEditReservation = (reservation) => {
    setModalMode('edit');
    setModalError('');
    setFieldErrors({});
    setOverlapConfirmPending(false);
    setEditingReservation(reservation);
    const initialData = {
      cele_meno: reservation.cele_meno,
      email: reservation.email,
      telefon: reservation.telefon,
      adresa: reservation.adresa || '',
      datum_rezervacie: reservation.datum_rezervacie,
      cas_rezervacie: reservation.cas_rezervacie,
      sluzba_id: reservation.sluzba_id,
      poznamka: reservation.poznamka || '',
      rezervacne_id: reservation.rezervacne_id || '',
    };
    setFormData(initialData);
    setOriginalFormData(initialData);
    setShowModal(true);
  };

  const handleDeleteReservation = (reservation) => {
    setConfirmModalTitle('Vymazať rezerváciu');
    setConfirmModalMessage(`Naozaj chcete natrvalo vymazať rezerváciu pre ${reservation.cele_meno} z databázy? Táto akcia sa nedá vrátiť späť.`);
    setConfirmModalAction(() => async () => {
      try {
        await deleteReservation(reservation.id);
        setReservations(prev => prev.filter(r => r.id !== reservation.id));
      } catch (err) {
        console.error('Failed to delete reservation:', err);
        setNotificationType('error');
        setNotificationMessage('Nepodarilo sa vymazať rezerváciu');
        setShowNotificationModal(true);
      }
    });
    setShowConfirmModal(true);
  };

  const handleCancelReservation = (id) => {
    setConfirmModalTitle('Zrušiť rezerváciu');
    setConfirmModalMessage('Naozaj si prajete zrušiť túto rezerváciu?');
    setConfirmModalAction(() => async () => {
      try {
        await cancelReservationAdmin(id);
        
        setReservations(prev => prev.map(r => 
          r.id === id ? { ...r, stav: 'cancelled', zrusene: true } : r
        ));
        
      } catch (err) {
        console.error('Failed to cancel reservation:', err);
        
        setNotificationType('error');
        setNotificationMessage('Nepodarilo sa zrušiť rezerváciu: ' + (err.response?.data?.message || err.message));
        setShowNotificationModal(true);
      }
    });
    setShowConfirmModal(true);
  };

  const handleConfirmReservation = async (id) => {
    setIsConfirming(true);
    try {
      const adminUser = localStorage.getItem('admin_user');
      let adminUserId = null;
      if (adminUser) {
        try {
          const userData = JSON.parse(adminUser);
          adminUserId = userData.id;
        } catch (err) {
          console.error('Failed to parse admin user:', err);
        }
      }

      await updateReservation(id, { 
        stav: 'confirmed',
        upravil_id: adminUserId 
      });
      
      setReservations(prev => prev.map(r => 
        r.id === id ? { ...r, stav: 'confirmed' } : r
      ));
    } catch (err) {
      console.error('Failed to confirm reservation:', err);
      setNotificationType('error');
      setNotificationMessage(err.response?.data?.message || 'Nepodarilo sa potvrdiť rezerváciu');
      setShowNotificationModal(true);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleRestoreReservation = async (id) => {
    try {
      const adminUser = localStorage.getItem('admin_user');
      let adminUserId = null;
      if (adminUser) {
        try {
          const userData = JSON.parse(adminUser);
          adminUserId = userData.id;
        } catch (err) {
          console.error('Failed to parse admin user:', err);
        }
      }

      await updateReservation(id, { 
        stav: 'confirmed',
        upravil_id: adminUserId 
      });
      
      setReservations(prev => prev.map(r => 
        r.id === id ? { ...r, stav: 'confirmed', zrusene: false } : r
      ));
    } catch (err) {
      console.error('Failed to restore reservation:', err);
      setNotificationType('error');
      setNotificationMessage(err.response?.data?.message || 'Nepodarilo sa obnoviť rezerváciu');
      setShowNotificationModal(true);
    }
  };


  const handleSaveReservation = async (e) => {
    e.preventDefault();
    
    setModalLoading(true);
    setModalError('');
    setFieldErrors({});
    
    const errors = {};
    
    const phoneRegex = /^[\d\s+\-()]{9,}$/;
    if (!phoneRegex.test(formData.telefon)) {
      errors.telefon = 'Telefónne číslo musí obsahovať minimálne 9 číslic (napr. +421 123 456 789)';
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      errors.email = 'Zadajte platnú emailovú adresu';
    }
    
    if (formData.cele_meno.trim().split(/\s+/).length < 2) {
      errors.cele_meno = 'Zadajte meno a priezvisko';
    }
    
    const warnings = {};
    if (formData.datum_rezervacie) {
      const [year, month, day] = formData.datum_rezervacie.split('-').map(Number);
      const selectedDate = new Date(year, month - 1, day);
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        warnings.datum_rezervacie = 'Dátum by nemal byť v minulosti';
      } else if (selectedDate.getTime() === today.getTime() && formData.cas_rezervacie) {
        const now = new Date();
        const [hours, minutes] = formData.cas_rezervacie.split(':');
        const selectedDateTime = new Date();
        selectedDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        
        if (selectedDateTime < now) {
          warnings.datum_rezervacie = 'Čas by nemal byť v minulosti';
        }
      }
    }
    
    setFieldWarnings(warnings);
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setModalError('Prosím, opravte chyby vo formulári');
      setModalLoading(false);
      return;
    }
    
    if (modalMode === 'edit' && originalFormData) {
      const hasChanges = Object.keys(formData).some(key => 
        formData[key] !== originalFormData[key]
      );
      
      if (!hasChanges) {
        setModalLoading(false);
        closeModalWithAnimation(() => {
          setShowModal(false);
          setEditingReservation(null);
          setFieldErrors({});
          setOriginalFormData(null);
        });
        return;
      }
    }
    
    try {
      if (modalMode === 'add') {
        const payload = overlapConfirmPending
          ? { ...formData, allow_overlap: true }
          : formData;
        const response = await createReservationAdmin(payload);
        const newReservation = response.data.data || response.data;
        setReservations(prev => [...prev, newReservation]);
      } else {
        const payload = overlapConfirmPending
          ? { ...formData, allow_overlap: true }
          : formData;
        await updateReservation(editingReservation.id, payload);
        
        setReservations(prev => prev.map(r => 
          r.id === editingReservation.id 
            ? { ...r, ...formData, nazov_sluzby: services.find(s => s.id === parseInt(formData.sluzba_id))?.nazov || r.nazov_sluzby }
            : r
        ));
      }
      
      setModalLoading(false);
      setOverlapConfirmPending(false);
      closeModalWithAnimation(() => {
        setShowModal(false);
        setEditingReservation(null);
        setFieldErrors({});
        setOriginalFormData(null);
      });
    } catch (err) {
      console.error('Failed to save reservation:', err);
      
      if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        const newFieldErrors = {};
        
        Object.keys(errors).forEach(key => {
          const message = Array.isArray(errors[key]) ? errors[key][0] : errors[key];
          newFieldErrors[key] = message;
        });
        
        setFieldErrors(newFieldErrors);
        setModalError('Prosím, opravte chyby vo formulári');
      } else {
        if (err.response?.data?.warning || err.response?.status === 409) {
          const warningMsg = err.response?.data?.message || 'Termíny sa prekrývajú s inou rezerváciou. Opätovným uložením zmenu potvrdíte.';
          setModalError(warningMsg);
          setOverlapConfirmPending(true);
        } else {
          const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message;
          setModalError('Nepodarilo sa uložiť rezerváciu: ' + errorMsg);
        }
      }
      setModalLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (['datum_rezervacie', 'cas_rezervacie', 'sluzba_id'].includes(name)) {
      setOverlapConfirmPending(false);
    }
    
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    validateField(name, value);
  };

  const validateField = (name, value) => {
    const errors = {};
    
    switch(name) {
      case 'telefon':
        const phoneRegex = /^[\d\s+\-()]{9,}$/;
        if (value && !phoneRegex.test(value)) {
          errors.telefon = 'Telefónne číslo musí obsahovať minimálne 9 číslic';
        }
        break;
        
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (value && !emailRegex.test(value)) {
          errors.email = 'Zadajte platnú emailovú adresu';
        }
        break;
        
      case 'cele_meno':
        if (value && value.trim().split(/\s+/).length < 2) {
          errors.cele_meno = 'Zadajte meno a priezvisko';
        }
        break;
        
      case 'adresa':
        const addressRegex = /^[^,]+\s+\d+[^,]*,\s*.+$/;
        if (value && !addressRegex.test(value)) {
          errors.adresa = 'Zadajte adresu vo formáte: Ulica číslo, Mesto';
        }
        break;
        
      default:
        break;
        
      case 'datum_rezervacie':
        if (value) {
          const [year, month, day] = value.split('-').map(Number);
          const selectedDate = new Date(year, month - 1, day);
          
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          if (selectedDate < today) {
            setFieldWarnings(prev => ({ ...prev, datum_rezervacie: 'Dátum by nemal byť v minulosti' }));
          } else if (selectedDate.getTime() === today.getTime() && formData.cas_rezervacie) {
            const now = new Date();
            const [hours, minutes] = formData.cas_rezervacie.split(':');
            const selectedDateTime = new Date();
            selectedDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
            
            if (selectedDateTime < now) {
              setFieldWarnings(prev => ({ ...prev, datum_rezervacie: 'Čas by nemal byť v minulosti' }));
            } else {
              setFieldWarnings(prev => {
                const newWarnings = { ...prev };
                delete newWarnings.datum_rezervacie;
                return newWarnings;
              });
            }
          } else {
            setFieldWarnings(prev => {
              const newWarnings = { ...prev };
              delete newWarnings.datum_rezervacie;
              return newWarnings;
            });
          }
        }
        break;
        
      case 'cas_rezervacie':
        if (value && formData.datum_rezervacie) {
          const selectedDate = new Date(formData.datum_rezervacie);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          if (selectedDate < today) {
            setFieldWarnings(prev => ({ ...prev, datum_rezervacie: 'Dátum by nemal byť v minulosti' }));
          } else if (selectedDate.getTime() === today.getTime()) {
            const [hours, minutes] = value.split(':');
            const selectedDateTime = new Date();
            selectedDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
            
            if (selectedDateTime < new Date()) {
              setFieldWarnings(prev => ({ ...prev, datum_rezervacie: 'Čas by nemal byť v minulosti' }));
            } else {
              setFieldWarnings(prev => {
                const newWarnings = { ...prev };
                delete newWarnings.datum_rezervacie;
                return newWarnings;
              });
            }
          } else {
            setFieldWarnings(prev => {
              const newWarnings = { ...prev };
              delete newWarnings.datum_rezervacie;
              return newWarnings;
            });
          }
        }
        break;
    }
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(prev => ({ ...prev, ...errors }));
    }
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(paginatedReservations.map(r => r.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkCancel = () => {
    if (selectedIds.length === 0) return;
    
    setConfirmModalTitle('Hromadné zrušenie rezervácií');
    setConfirmModalMessage(`Naozaj chcete zrušiť ${selectedIds.length} ${selectedIds.length === 1 ? 'rezerváciu' : selectedIds.length < 5 ? 'rezervácie' : 'rezervácií'}?`);
    setConfirmModalAction(() => async () => {
      try {
        const promises = selectedIds.map(id => cancelReservationAdmin(id));
        await Promise.all(promises);
        
        setReservations(prev => prev.map(r => 
          selectedIds.includes(r.id) ? { ...r, stav: 'cancelled', zrusene: true } : r
        ));
        
        setSelectedIds([]);
      } catch (err) {
        console.error('Failed to cancel reservations:', err);
        
        setNotificationType('error');
        setNotificationMessage('Nepodarilo sa zrušiť všetky rezervácie: ' + (err.response?.data?.message || err.message));
        setShowNotificationModal(true);
      }
    });
    setShowConfirmModal(true);
  };

  const handleCalendarEventClick = (reservation) => {
    handleEditReservation(reservation);
  };

  const handleCalendarDateSelect = (selectInfo) => {
    const selectedDate = selectInfo.start.toISOString().split('T')[0];
    const selectedTime = selectInfo.start.toTimeString().slice(0, 5);
    
    setModalMode('add');
    setModalError('');
    setFieldErrors({});
    setFormData({
      cele_meno: '',
      email: '',
      telefon: '',
      adresa: '',
      datum_rezervacie: selectedDate,
      cas_rezervacie: selectedTime,
      sluzba_id: '',
      poznamka: '',
    });
    setShowModal(true);
  };

  const handleCalendarEventDrop = async (reservation, newDate, newTime) => {
    try {
      await updateReservation(reservation.id, {
        ...reservation,
        datum_rezervacie: newDate,
        cas_rezervacie: newTime
      });
      loadReservations();
    } catch (err) {
      console.error('Failed to update reservation time:', err);
      alert('Nepodarilo sa zmeniť čas rezervácie');
    }
  };

  const filteredReservations = sortReservations(
    baseFilteredReservations.filter((reservation) => matchesTabFilter(reservation, filter))
  );
  const totalPages = Math.ceil(filteredReservations.length / itemsPerPage);
  const paginatedReservations = filteredReservations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStatusBadge = (reservation) => {
    switch(reservation.stav) {
      case 'pending':
        return <span className="badge badge-warning">Nepotvrdené</span>;
      case 'confirmed':
        return <span className="badge badge-info">Potvrdené</span>;
      case 'cancelled':
        return <span className="badge badge-danger">Zrušené</span>;
      case 'completed':
        return <span className="badge badge-success">Dokončené</span>;
      default:
        if (reservation.zrusene) {
          return <span className="badge badge-danger">Zrušené</span>;
        }
        const reservationDate = new Date(reservation.datum_rezervacie);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        reservationDate.setHours(0, 0, 0, 0);
        
        if (reservationDate < today) {
          return <span className="badge badge-secondary">Vybavené</span>;
        }
        return <span className="badge badge-info">Potvrdené</span>;
    }
  };

  const formatCreatedAt = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return {
      date: date.toLocaleDateString('sk-SK'),
      time: date.toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit' })
    };
  };

  if (loading) {
    return (
      <AdminPageSkeleton
        title="Správa rezervácií"
        subtitle="Prehľad a správa všetkých rezervácií"
        variant="reservations"
      />
    );
  }

  if (serverError) {
    return <Error500 />;
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="alert alert-error mb-3">{error}</div>
      </div>
    );
  }

  return (
    <div className="admin-page admin-reservations-page">
      {isConfirming && (
        <div className="page-overlay">
          <div className="page-overlay-card">
            <div className="spinner"></div>
            <p>Potvrdzujem rezerváciu...</p>
          </div>
        </div>
      )}
      <div className="page-header">
        <div>
          <h1>Správa rezervácií</h1>
          <p className="text-muted">Prehľad a správa všetkých rezervácií</p>
        </div>
        <div className="header-actions">
          <div className="view-toggle">
            <button 
              className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Tabuľkové zobrazenie"
            >
              <span className="icon">
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2zm15 2h-4v3h4V4zm0 4h-4v3h4V8zm0 4h-4v3h3a1 1 0 0 0 1-1v-2zm-5 3v-3H6v3h4zm-5 0v-3H1v2a1 1 0 0 0 1 1h3zm-4-4h4V8H1v3zm0-4h4V4H1v3zm5-3v3h4V4H6zm4 4H6v3h4V8z"/>
                </svg>
              </span>
              <span className="toggle-label">Tabuľka</span>
            </button>
            <button 
              className={`toggle-btn ${viewMode === 'calendar' ? 'active' : ''}`}
              onClick={() => setViewMode('calendar')}
              title="Kalendárne zobrazenie"
            >
              <span className="icon">
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/>
                </svg>
              </span>
              <span className="toggle-label">Kalendár</span>
            </button>
          </div>
          <button className="btn btn-primary btn-lg" onClick={handleAddReservation}>Vytvoriť rezerváciu</button>
        </div>
      </div>

      <div className={`smart-tabs ${viewMode === 'calendar' ? 'tabs-rounded' : ''}`}>
        <div className="tabs-container">
          <button 
            className={`tab-btn ${filter === 'all' ? 'active active-all' : ''}`} 
            onClick={() => setFilter('all')}
          >
            <span className="tab-indicator tab-indicator-gray"></span>
            Všetky
            <span className="tab-count">{counts.all}</span>
          </button>
          {(counts.pending > 0 || filter === 'pending') && (
            <button 
              className={`tab-btn ${filter === 'pending' ? 'active active-pending' : ''}`} 
              onClick={() => setFilter('pending')}
            >
              <span className="tab-indicator tab-indicator-orange"></span>
              Nepotvrdené
              <span className="tab-count">{counts.pending}</span>
            </button>
          )}
          {(counts.upcoming > 0 || filter === 'upcoming') && (
            <button 
              className={`tab-btn ${filter === 'upcoming' ? 'active active-upcoming' : ''}`} 
              onClick={() => setFilter('upcoming')}
            >
              <span className="tab-indicator tab-indicator-green"></span>
              Potvrdené
              <span className="tab-count">{counts.upcoming}</span>
            </button>
          )}
          {(counts.past > 0 || filter === 'past') && (
            <button 
              className={`tab-btn ${filter === 'past' ? 'active active-past' : ''}`} 
              onClick={() => setFilter('past')}
            >
              <span className="tab-indicator tab-indicator-blue"></span>
              Dokončené
              <span className="tab-count">{counts.past}</span>
            </button>
          )}
          {(counts.cancelled > 0 || filter === 'cancelled') && (
            <button 
              className={`tab-btn ${filter === 'cancelled' ? 'active active-cancelled' : ''}`} 
              onClick={() => setFilter('cancelled')}
            >
              <span className="tab-indicator tab-indicator-red"></span>
              Zrušené
              <span className="tab-count">{counts.cancelled}</span>
            </button>
          )}
        </div>
      </div>

      <div className={`advanced-filters ${viewMode === 'calendar' ? 'filters-hidden' : ''}`}>
        <div className="filter-row">
          <div className="form-group search-main">
            <svg className="search-icon" width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              className="form-input search-input"
              placeholder="Hľadať globálne medzi rezerváciami..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="form-group">
            <select
              className="form-select"
              value={quickFilterValue}
              onChange={handleQuickFilterChange}
            >
              <option value="">Kedykoľvek</option>
              <option value="today">Dnes</option>
              <option value="tomorrow">Zajtra</option>
              <option value="week">Tento týždeň</option>
              <option value="month">Tento mesiac</option>
            </select>
          </div>
          <div className="form-group">
            <input
              type={dateFrom ? 'date' : 'text'}
              className="form-input"
              placeholder="Od dátumu"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              onFocus={(e) => e.target.type = 'date'}
              onBlur={(e) => !e.target.value && (e.target.type = 'text')}
            />
          </div>
          <div className="form-group">
            <input
              type={dateTo ? 'date' : 'text'}
              className="form-input"
              placeholder="Do dátumu"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              onFocus={(e) => e.target.type = 'date'}
              onBlur={(e) => !e.target.value && (e.target.type = 'text')}
            />
          </div>
          <div className="form-group">
            <select
              className="form-select"
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
            >
              <option value="all">Všetky služby</option>
              {services.map(service => (
                <option key={service.id} value={service.id}>{service.nazov}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {viewMode === 'table' ? (
        <>
          <div className="table-container">
            {paginatedReservations.length > 0 ? (
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="th-checkbox">
                        <input
                          type="checkbox"
                          onChange={handleSelectAll}
                          checked={paginatedReservations.length > 0 && selectedIds.length === paginatedReservations.length}
                        />
                      </th>
                      <th 
                        className="sortable"
                        onClick={() => handleSort('datum_cas')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        Termín {sortField === 'datum_cas' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th 
                        className="sortable"
                        onClick={() => handleSort('cele_meno')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        Zákazník {sortField === 'cele_meno' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th 
                        className="sortable"
                        onClick={() => handleSort('sluzba_nazov')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        Služba {sortField === 'sluzba_nazov' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th 
                        className="sortable"
                        onClick={() => handleSort('stav')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        Stav {sortField === 'stav' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th>Telefón</th>
                      <th>Adresa</th>
                      <th>Číslo rezervácie</th>
                      <th
                        className="sortable"
                        onClick={() => handleSort('vytvorene')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        Vytvorená {sortField === 'vytvorene' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th>Akcie</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedReservations.map((reservation) => (
                      <tr key={reservation.id} className={selectedIds.includes(reservation.id) ? 'selected' : ''}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(reservation.id)}
                            onChange={() => handleSelectOne(reservation.id)}
                          />
                        </td>
                        <td className="td-center">
                          <div className="datetime-info">
                            <strong>{new Date(reservation.datum_rezervacie).toLocaleDateString('sk-SK')}</strong>
                            <span className="time-info">{reservation.cas_rezervacie}</span>
                          </div>
                        </td>
                        <td>
                          <div className="customer-info">
                            <div>
                              <div className="customer-name">
                                <strong>{reservation.cele_meno}</strong>
                                {reservation.poznamka && (
                                  <span className="note-icon-wrapper">
                                    <span className="note-icon">
                                      <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                                        <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                        <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                      </svg>
                                    </span>
                                    <span className="note-tooltip">{reservation.poznamka}</span>
                                  </span>
                                )}
                              </div>
                              <span className="customer-email">
                                <a href={`mailto:${reservation.email}`} className="clickable-link">
                                  {reservation.email}
                                </a>
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="td-center">{reservation.nazov_sluzby || 'N/A'}</td>
                        <td className="td-center">{getStatusBadge(reservation)}</td>
                        <td className="td-center">
                          <a href={`tel:${reservation.telefon}`} className="clickable-link">
                            {formatPhoneNumber(reservation.telefon)}
                          </a>
                        </td>
                        <td className="td-center">{reservation.adresa}</td>
                        <td className="td-center">
                          {reservation.rezervacne_id && (
                            <span className="reservation-id-badge">{reservation.rezervacne_id}</span>
                          )}
                        </td>
                        <td className="td-center">
                          {(() => {
                          const createdAt = formatCreatedAt(
                            reservation.vytvorene || reservation.created_at || reservation.createdAt
                          );
                            if (!createdAt) {
                              return <span className="text-muted">—</span>;
                            }
                            return (
                              <div className="datetime-info">
                                <strong>{createdAt.date}</strong>
                                <span className="time-info">{createdAt.time}</span>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="td-center">
                          <div className="actions-dropdown">
                            <button
                              className="btn-icon"
                              type="button"
                              aria-haspopup="menu"
                              aria-expanded={openActionId === reservation.id}
                              onClick={(event) => {
                                event.stopPropagation();
                                const rect = event.currentTarget.getBoundingClientRect();
                                const menuWidth = 220;
                                const menuHeight = 300;
                                const viewportWidth = window.innerWidth;
                                const viewportHeight = window.innerHeight;
                                const availableRight = viewportWidth - rect.right;
                                const availableBottom = viewportHeight - rect.bottom;
                                const availableTop = rect.top;
                                const shouldFlipLeft = availableRight < menuWidth + 12;
                                const shouldFlipUp = availableBottom < menuHeight + 12 && availableTop >= menuHeight + 12;
                                const translateX = shouldFlipLeft ? '-100%' : '0';
                                const translateY = shouldFlipUp ? '-100%' : '0';
                                setActionMenuPosition({
                                  top: shouldFlipUp ? rect.top - 8 : rect.bottom + 8,
                                  left: shouldFlipLeft ? rect.right : rect.left,
                                  transform: `translate(${translateX}, ${translateY})`
                                });
                                setOpenActionId(prev => prev === reservation.id ? null : reservation.id);
                              }}
                              title="Akcie"
                            >
                              <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M3 9.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z"/>
                              </svg>
                            </button>
                            {openActionId === reservation.id && (
                              <div
                                className="dropdown-menu dropdown-menu-fixed"
                                role="menu"
                                style={{
                                  top: `${actionMenuPosition.top}px`,
                                  left: `${actionMenuPosition.left}px`,
                                  transform: actionMenuPosition.transform
                                }}
                              >
                                <button
                                  className="dropdown-item action-navigate"
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    const encodedAddress = encodeURIComponent(reservation.adresa || '');
                                    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
                                    setOpenActionId(null);
                                  }}
                                >
                                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                    <path fillRule="evenodd" d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
                                  </svg>
                                  Navigovať
                                </button>
                                <button
                                  className="dropdown-item action-edit"
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleEditReservation(reservation);
                                    setOpenActionId(null);
                                  }}
                                >
                                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M20.9888 4.28491L19.6405 2.93089C18.4045 1.6897 16.4944 1.6897 15.2584 2.93089L13.0112 5.30042L18.7416 11.055L21.1011 8.68547C21.6629 8.1213 22 7.33145 22 6.54161C22 5.75176 21.5506 4.84908 20.9888 4.28491Z"/>
                                    <path d="M16.2697 10.9422L11.7753 6.42877L2.89888 15.3427C2.33708 15.9069 2 16.6968 2 17.5994V21.0973C2 21.5487 2.33708 22 2.89888 22H6.49438C7.2809 22 8.06742 21.6615 8.74157 21.0973L17.618 12.1834L16.2697 10.9422Z"/>
                                  </svg>
                                  Upraviť
                                </button>
                                {reservation.stav === 'pending' && (
                                  <button
                                    className="dropdown-item action-confirm"
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleConfirmReservation(reservation.id);
                                      setOpenActionId(null);
                                    }}
                                  >
                                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                                    </svg>
                                    Potvrdiť
                                  </button>
                                )}
                                {reservation.stav === 'cancelled' && (
                                  <button
                                    className="dropdown-item action-restore"
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleRestoreReservation(reservation.id);
                                      setOpenActionId(null);
                                    }}
                                  >
                                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                      <path fillRule="evenodd" d="M8 3a5 5 0 1 1-4.546 2.914.5.5 0 0 0-.908-.417A6 6 0 1 0 8 2v1z"/>
                                      <path d="M8 4.466V.534a.25.25 0 0 0-.41-.192L5.23 2.308a.25.25 0 0 0 0 .384l2.36 1.966A.25.25 0 0 0 8 4.466z"/>
                                    </svg>
                                    Obnoviť
                                  </button>
                                )}
                                {!reservation.zrusene && reservation.stav !== 'cancelled' && (
                                  <button
                                    className="dropdown-item action-cancel"
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleCancelReservation(reservation.id);
                                      setOpenActionId(null);
                                    }}
                                  >
                                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
                                    </svg>
                                    Zrušiť
                                  </button>
                                )}
                                <button
                                  className="dropdown-item action-delete"
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleDeleteReservation(reservation);
                                    setOpenActionId(null);
                                  }}
                                >
                                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z"/>
                                    <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3h11V2h-11v1Z"/>
                                  </svg>
                                  Vymazať
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <p>Zatiaľ žiadne rezervácie</p>
              </div>
            )}
          </div>

          {selectedIds.length > 0 && (
            <div className="bulk-actions-bottom">
              <button className="btn btn-danger" onClick={handleBulkCancel}>
                Zrušiť označené rezervácie ({selectedIds.length})
              </button>
            </div>
          )}

          {/* TODO: zistit, kedy sa toto používa a či to nie je zbytočné */}
          {totalPages > 1 && (
            <div className="pagination">
              <button className="btn btn-sm btn-secondary" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                Predchádzajúca
              </button>
              <span className="pagination-info">Strana {currentPage} z {totalPages}</span>
              <button className="btn btn-sm btn-secondary" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                Ďalšia
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="calendar-view">
          <ReservationCalendar
            reservations={filteredReservations}
            services={services}
            blockedTimes={blockedTimes}
            onEventClick={handleCalendarEventClick}
            onDateSelect={handleCalendarDateSelect}
            onEventDrop={handleCalendarEventDrop}
            slotMinTime={calendarSettings.slotMinTime}
            slotMaxTime={calendarSettings.slotMaxTime}
            workingHours={workingHours}
          />
        </div>
      )}

      {/* Modal for Add/Edit Reservation */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modalMode === 'add' ? 'Vytvoriť rezerváciu' : 'Upraviť rezerváciu'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <form onSubmit={handleSaveReservation}>
              <div className="modal-body">
                {modalError && (
                  <div className={`alert ${overlapConfirmPending ? 'alert-warning' : 'alert-error'} mb-3`}>
                    <span className="alert-icon" aria-hidden="true">
                      {overlapConfirmPending ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                          <line x1="12" y1="9" x2="12" y2="13"/>
                          <line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="15" y1="9" x2="9" y2="15"/>
                          <line x1="9" y1="9" x2="15" y2="15"/>
                        </svg>
                      )}
                    </span>
                    <span>{modalError}</span>
                  </div>
                )}
                
                <div className="form-grid-modern">
                  {/* Celé meno */}
                  <div className={`form-group-floating ${formData.cele_meno ? 'has-value' : ''} ${fieldErrors.cele_meno || fieldErrors.customer_name ? 'has-error' : ''}`}>
                    <input
                      type="text"
                      name="cele_meno"
                      id="cele_meno"
                      className="form-input-floating"
                      value={formData.cele_meno}
                      onChange={handleFormChange}
                      onBlur={handleFieldBlur}
                      placeholder=" "
                      required
                    />
                    <label htmlFor="cele_meno" className="form-label-floating">Celé meno</label>
                    {(fieldErrors.cele_meno || fieldErrors.customer_name) && (
                      <span className="field-error">{fieldErrors.cele_meno || fieldErrors.customer_name}</span>
                    )}
                  </div>

                  {/* Email */}
                  <div className={`form-group-floating ${formData.email ? 'has-value' : ''} ${fieldErrors.email || fieldErrors.customer_email ? 'has-error' : ''}`}>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      className="form-input-floating"
                      value={formData.email}
                      onChange={handleFormChange}
                      onBlur={handleFieldBlur}
                      placeholder=" "
                      pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                      title="Zadajte platnú emailovú adresu"
                      required
                    />
                    <label htmlFor="email" className="form-label-floating">Email</label>
                    {(fieldErrors.email || fieldErrors.customer_email) && (
                      <span className="field-error">{fieldErrors.email || fieldErrors.customer_email}</span>
                    )}
                  </div>

                  {/* Telefón */}
                  <div className={`form-group-floating ${formData.telefon ? 'has-value' : ''} ${fieldErrors.telefon || fieldErrors.customer_phone ? 'has-error' : ''}`}>
                    <input
                      type="tel"
                      name="telefon"
                      id="telefon"
                      className="form-input-floating"
                      value={formData.telefon}
                      onChange={handleFormChange}
                      onBlur={handleFieldBlur}
                      placeholder=" "
                      pattern="[\d\s\+\-\(\)]{9,}"
                      title="Zadajte telefónne číslo (minimálne 9 číslic)"
                      required
                    />
                    <label htmlFor="telefon" className="form-label-floating">Telefónne číslo</label>
                    {(fieldErrors.telefon || fieldErrors.customer_phone) && (
                      <span className="field-error">{fieldErrors.telefon || fieldErrors.customer_phone}</span>
                    )}
                  </div>

                  {/* Adresa */}
                  <AddressAutocompleteField
                    value={formData.adresa}
                    onChange={handleFormChange}
                    onBlur={handleFieldBlur}
                    name="adresa"
                    id="adresa"
                    label="Adresa, Mesto"
                    title="Zadajte adresu alebo vyberte navrh zo zoznamu"
                    required
                    hasError={Boolean(fieldErrors.adresa || fieldErrors.customer_address)}
                    errorMessage={fieldErrors.adresa || fieldErrors.customer_address || ''}
                  />

                  {/* Služba */}
                  <div className={`form-group-floating ${formData.sluzba_id ? 'has-value' : ''} ${fieldErrors.sluzba_id || fieldErrors.service_id ? 'has-error' : ''}`}>
                    <select
                      name="sluzba_id"
                      id="sluzba_id"
                      className="form-input-floating"
                      value={formData.sluzba_id}
                      onChange={handleFormChange}
                      required
                    >
                      <option value=""></option>
                      {services.map(service => (
                        <option key={service.id} value={service.id}>{service.nazov}</option>
                      ))}
                    </select>
                    <label htmlFor="sluzba_id" className="form-label-floating">Vyberte službu</label>
                    {(fieldErrors.sluzba_id || fieldErrors.service_id) && (
                      <span className="field-error">{fieldErrors.sluzba_id || fieldErrors.service_id}</span>
                    )}
                  </div>

                  <div className={`form-group-floating ${formData.rezervacne_id ? 'has-value' : ''}`}>
                    <input
                      type="text"
                      name="rezervacne_id"
                      id="rezervacne_id"
                      className="form-input-floating"
                      value={formData.rezervacne_id || ''}
                      placeholder=" "
                      readOnly
                    />
                    <label htmlFor="rezervacne_id" className="form-label-floating">Rezervačné číslo</label>
                  </div>

                  {/* Dátum a Čas vedľa seba */}
                  <div className="datetime-group">
                    <div className={`form-group-floating ${formData.datum_rezervacie ? 'has-value' : ''} ${fieldErrors.datum_rezervacie || fieldErrors.reservation_date ? 'has-error' : ''} ${!fieldErrors.datum_rezervacie && fieldWarnings.datum_rezervacie ? 'has-warning' : ''}`}>
                      <input
                        type="date"
                        name="datum_rezervacie"
                        id="datum_rezervacie"
                        className="form-input-floating"
                        value={formData.datum_rezervacie}
                        onChange={handleFormChange}
                        onBlur={handleFieldBlur}
                        placeholder=" "
                        required
                      />
                      <label htmlFor="datum_rezervacie" className="form-label-floating">Dátum</label>
                      {(fieldErrors.datum_rezervacie || fieldErrors.reservation_date) && (
                        <span className="field-error">{fieldErrors.datum_rezervacie || fieldErrors.reservation_date}</span>
                      )}
                      {!fieldErrors.datum_rezervacie && !fieldErrors.reservation_date && fieldWarnings.datum_rezervacie && (
                        <span className="field-warning">{fieldWarnings.datum_rezervacie}</span>
                      )}
                    </div>

                    <div className={`form-group-floating ${formData.cas_rezervacie ? 'has-value' : ''} ${fieldErrors.cas_rezervacie || fieldErrors.reservation_time ? 'has-error' : ''}`}>
                      <input
                        type="time"
                        name="cas_rezervacie"
                        id="cas_rezervacie"
                        className="form-input-floating"
                        value={formData.cas_rezervacie}
                        onChange={handleFormChange}
                        onBlur={handleFieldBlur}
                        placeholder=" "
                        required
                      />
                      <label htmlFor="cas_rezervacie" className="form-label-floating">Čas</label>
                      {(fieldErrors.cas_rezervacie || fieldErrors.reservation_time) && (
                        <span className="field-error">{fieldErrors.cas_rezervacie || fieldErrors.reservation_time}</span>
                      )}
                    </div>
                  </div>

                  {/* Poznámka */}
                  <div className={`form-group-floating full-width ${formData.poznamka ? 'has-value' : ''}`}>
                    <textarea
                      name="poznamka"
                      id="poznamka"
                      className="form-input-floating"
                      rows="3"
                      value={formData.poznamka}
                      onChange={handleFormChange}
                      placeholder=" "
                      style={{ resize: "none" }}
                    />
                    <label htmlFor="poznamka" className="form-label-floating">Poznámka <span className="optional">(voliteľné)</span></label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => {
                  handleCloseModal();
                  setFieldErrors({});
                }}>
                  Zrušiť
                </button>
                <button type="submit" className="btn btn-primary" disabled={modalLoading}>
                  {modalLoading ? 'Ukladám...' : (modalMode === 'add' ? 'Vytvoriť' : 'Uložiť zmeny')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        show={showConfirmModal}
        onClose={handleCloseConfirmModal}
        onConfirm={() => {
          handleCloseConfirmModal();
          if (confirmModalAction) {
            confirmModalAction();
          }
        }}
        title={confirmModalTitle}
        message={confirmModalMessage}
        confirmText="Potvrdiť"
        cancelText="Zrušiť"
        confirmButtonClass="btn-danger"
      />

      {/* Notification Modal */}
      {showNotificationModal && (
        <div className="modal-overlay" onClick={handleCloseNotificationModal}>
          <div className="modal-content modal-notification" onClick={(e) => e.stopPropagation()}>
            <div className={`modal-header modal-header-${notificationType}`}>
              <h2>{notificationType === 'success' ? 'Úspech' : 'Chyba'}</h2>
              <button className="modal-close" onClick={handleCloseNotificationModal}>×</button>
            </div>
            <div className="modal-body">
              <p className="notification-message">{notificationMessage}</p>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className={`btn ${notificationType === 'success' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={handleCloseNotificationModal}
              >
                Zavrieť
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReservationsPage;
